import { Button, Label, toast } from "@devhop/ui";
import { Plus, X } from "lucide-react";
import { useRef, useState } from "react";
import { config } from "@/shared/lib/config";
import { uploadApi } from "@/shared/lib/upload-api";
import { uploadFileToPresignedUrl } from "@/shared/lib/upload-to-presigned";
import { resolveImageSrc } from "@/shared/ui/AppImage";

const ACCEPT = "image/*";
const KEY_PREFIX = "uploads/customer-display-qr";
const MAX_SIZE_MB = 2;

/** Aspect ratio 4:5 สำหรับ QR ธนาคาร */
const QR_ASPECT_RATIO = "aspect-[4/5]";

type CustomerDisplayQrListFieldProps = {
  value: string[];
  onChange: (urls: string[]) => void;
  branchId: string | null;
  disabled?: boolean;
};

export function CustomerDisplayQrListField({
  value,
  onChange,
  branchId,
  disabled,
}: CustomerDisplayQrListFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !branchId) return;
    e.target.value = "";

    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      toast.error(`ຂະໜາດໄຟລ໌ເກີນ ${MAX_SIZE_MB}MB`);
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast.error("ກະລຸນາເລືອກໄຟລ໌ຮູບພາບເທົ່ານັ້ນ");
      return;
    }

    setUploading(true);
    try {
      const ext = file.name.includes(".")
        ? `.${file.name.split(".").pop() ?? ""}`
        : ".png";
      const key = `${KEY_PREFIX}/${branchId}/${Date.now()}${ext}`;
      const contentType = file.type || "image/png";

      const { uploadUrl } = await uploadApi.getPresignUrl({
        key,
        contentType,
        expiresIn: 900,
      });
      await uploadFileToPresignedUrl(uploadUrl, file, { contentType });

      const newUrl = config.filesUrl(key);
      onChange([...value, newUrl]);
      toast.success("ເພີ່ມຮູບ QR ສຳເລັດ");
    } catch {
      toast.error("ອັບໂຫຼດບໍ່ສຳເລັດ");
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handleAdd = () => {
    if (disabled || uploading || !branchId) return;
    inputRef.current?.click();
  };

  return (
    <div className="space-y-2">
      <Label>ຮູບ QR ຈ່າຍເງິນທະນາຄານ</Label>
      <p className="text-muted-foreground text-xs">
        ເພີ່ມຫຼາຍຮູບໄດ້ (ອັດຕາສ່ວນ 4:5). ສະແດງໃນຫນ້າຈໍລູກຄ້າ
      </p>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={handleFileChange}
      />

      <div className="flex flex-wrap gap-4">
        {value.map((url, index) => (
          <div
            key={url}
            className={`relative flex shrink-0 flex-col overflow-hidden rounded border bg-muted ${QR_ASPECT_RATIO} w-24`}
          >
            <img
              src={resolveImageSrc(url) ?? undefined}
              alt={`QR ${index + 1}`}
              className="size-full object-contain"
            />
            {!disabled && (
              <Button
                type="button"
                variant="secondary"
                size="icon"
                className="absolute top-1 right-1 h-6 w-6 rounded-full"
                onClick={() => handleRemove(index)}
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>
        ))}

        {!disabled && (
          <button
            type="button"
            onClick={handleAdd}
            disabled={uploading || !branchId}
            className={`flex w-24 shrink-0 flex-col items-center justify-center gap-1 rounded border border-dashed bg-muted/50 transition-colors hover:bg-muted ${QR_ASPECT_RATIO} disabled:opacity-50`}
          >
            {uploading ? (
              <span className="text-muted-foreground text-xs">
                ກຳລັງອັບໂຫຼດ...
              </span>
            ) : (
              <>
                <Plus className="h-6 w-6 text-muted-foreground" />
                <span className="text-muted-foreground text-xs">ເພີ່ມ</span>
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
